import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import type { PlatformAppId } from "../config/environment";
import { MockApiError } from "../services/mockApi";
import { createMockRepository } from "../services/mockRepository";

function StartupState({ error }: { error?: MockApiError }) {
  const empty = !error;
  return (
    <main
      role={empty ? "status" : "alert"}
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#060716",
        color: "#f7f8ff",
        font: "16px/1.5 Inter, system-ui, sans-serif",
      }}
    >
      <section style={{ maxWidth: 560 }}>
        <p style={{ color: "#2fd6f4", fontWeight: 800 }}>{empty ? "EMPTY_STATE" : error.code}</p>
        <h1>{empty ? "No workspace data is available yet." : "We could not load this workspace."}</h1>
        <p>
          {empty
            ? "The mock service returned an empty result. Add mock records or return to the normal scenario."
            : error.message}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            minHeight: 44,
            padding: "0 16px",
            border: 0,
            borderRadius: 10,
            background: "#8b5cf6",
            color: "white",
            font: "inherit",
            fontWeight: 800,
          }}
        >
          {empty ? "Refresh data" : "Try again"}
        </button>
      </section>
    </main>
  );
}

export async function mountFrontend(root: HTMLElement, application: ReactNode, app: PlatformAppId) {
  root.setAttribute("aria-busy", "true");
  root.dataset.app = app;

  try {
    const runtime = await createMockRepository(app, `${app}:runtime`, [{ id: "ready", ready: true }]).list({
      pageSize: 1,
    });
    root.removeAttribute("aria-busy");
    createRoot(root).render(runtime.items.length ? application : <StartupState />);
  } catch (cause) {
    const error =
      cause instanceof MockApiError
        ? cause
        : new MockApiError("Unable to start the app.", 500, "BOOT_FAILED");
    root.removeAttribute("aria-busy");
    createRoot(root).render(<StartupState error={error} />);
  }
}
